import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';

/**
 * Discord Onboarding Tour Component
 * Hybrid approach: Custom React component with tour-like functionality
 * Guides users through Discord connection setup
 */
const DiscordOnboardingTour = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showUsernameHelper, setShowUsernameHelper] = useState(false);
  const [completedSteps, setCompletedSteps] = useState({});
  
  // Touch/Swipe handling
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const steps = [
    {
      id: 'welcome',
      title: '🎉 Welcome to Discord Integration!',
      description: 'Get instant notifications when you receive new messages. Let\'s set this up in just a few steps.',
      action: null,
      skippable: false
    },
    {
      id: 'join-server',
      title: '🚀 Step 1: Join Our Discord Server',
      description: 'Click the button below to join our Discord server. This allows our bot to find you and send notifications.',
      action: {
        type: 'button',
        text: 'Join Discord Server',
        onClick: () => {
          window.open('https://discord.gg/eZYmqxexRW', '_blank');
          markStepCompleted('join-server');
        }
      },
      tip: '💡 You\'ll need to be in our server for the bot to send you DMs'
    },
    {
      id: 'enable-dms',
      title: '⚙️ Step 2: Enable Direct Messages',
      description: 'In Discord, go to Settings → Privacy & Safety → Enable "Allow direct messages from server members"',
      action: {
        type: 'guide',
        steps: [
          '1. Click the ⚙️ gear icon in Discord (User Settings)',
          '2. Go to "Privacy & Safety" in the left menu',
          '3. Under "Server Privacy Defaults"',
          '4. Enable "Allow direct messages from server members"'
        ]
      },
      tip: '🔒 This lets our bot send you notification DMs'
    },
    {
      id: 'username-input',
      title: '👤 Step 3: Enter Your Discord Username',
      description: 'Enter your Discord username exactly as it appears in Discord. Case-sensitive!',
      action: {
        type: 'input-guide',
        examples: [
          '✅ micky16270',
          '✅ JohnDoe',
          '✅ user.name',
          '✅ username#1234'
        ],
        mistakes: [
          '❌ Wrong capitalization',
          '❌ Extra spaces',
          '❌ Missing # numbers'
        ]
      },
      tip: '🎯 Copy your username directly from Discord to avoid typos'
    },
    {
      id: 'test-connection',
      title: '🧪 Step 4: Test Your Connection',
      description: 'Click the test button to verify everything works. You should receive a DM in Discord!',
      action: {
        type: 'highlight',
        element: 'test-button'
      },
      tip: '📱 Check your Discord app for the test message'
    },
    {
      id: 'success',
      title: '🎉 All Set!',
      description: 'Perfect! You\'ll now receive Discord notifications when you get new messages. You can always come back here to update your settings.',
      action: null,
      skippable: false
    }
  ];

  const markStepCompleted = (stepId) => {
    setCompletedSteps(prev => ({
      ...prev,
      [stepId]: true
    }));
  };

  // Swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentStep < steps.length - 1) {
      nextStep();
    }
    if (isRightSwipe && currentStep > 0) {
      prevStep();
    }
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
      onClose();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTour = () => {
    onClose();
  };

  const currentStepData = steps[currentStep];

  if (!isOpen) return null;

  return (
    <>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#7289DA]/20 rounded-lg flex items-center justify-center">
                <Icon name="MessageCircle" size={20} className="text-[#7289DA]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Discord Setup Guide</h2>
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      {steps.map((_, index) => (
                        <div
                          key={index}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentStep
                              ? 'bg-[#7289DA]'
                              : index < currentStep
                              ? 'bg-green-400'
                              : 'bg-white/20'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-white/60 text-sm">
                      Step {currentStep + 1} of {steps.length}
                    </span>
                  </div>
                  
                  {/* Swipe indicator - only show on mobile */}
                  <div className="md:hidden flex items-center space-x-1 text-white/40 text-xs">
                    <Icon name="ChevronLeft" size={12} />
                    <span>Swipe</span>
                    <Icon name="ChevronRight" size={12} />
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Content */}
          <div 
            className="p-6"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <h3 className="text-2xl font-semibold text-white mb-4">
                  {currentStepData.title}
                </h3>
              
                <p className="text-white/80 text-lg mb-6 leading-relaxed">
                  {currentStepData.description}
                </p>

                {/* Step-specific content */}
                {currentStepData.action?.type === 'button' && (
                  <div className="mb-6">
                    <button
                      onClick={currentStepData.action.onClick}
                      className="bg-[#7289DA] hover:bg-[#677BC4] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Icon name="ExternalLink" size={16} />
                      <span>{currentStepData.action.text}</span>
                    </button>
                  </div>
                )}

                {currentStepData.action?.type === 'guide' && (
                  <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
                    <h4 className="text-white font-medium mb-3">📋 Follow these steps:</h4>
                    <ul className="space-y-2">
                      {currentStepData.action.steps.map((step, index) => (
                        <li key={index} className="text-white/80 flex items-start space-x-2">
                          <span className="text-[#7289DA] font-medium">{step.split('.')[0]}.</span>
                          <span>{step.split('.').slice(1).join('.')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {currentStepData.action?.type === 'input-guide' && (
                  <div className="mb-6 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                        <h4 className="text-green-300 font-medium mb-2">✅ Examples that work:</h4>
                        <ul className="space-y-1">
                          {currentStepData.action.examples.map((example, index) => (
                            <li key={index} className="text-green-200 text-sm font-mono">
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                        <h4 className="text-red-300 font-medium mb-2">❌ Common mistakes:</h4>
                        <ul className="space-y-1">
                          {currentStepData.action.mistakes.map((mistake, index) => (
                            <li key={index} className="text-red-200 text-sm">
                              {mistake}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowUsernameHelper(true)}
                      className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
                    >
                      <Icon name="HelpCircle" size={16} />
                      <span>How do I find my Discord username?</span>
                    </button>
                  </div>
                )}

                {currentStepData.tip && (
                  <div className="mb-6 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                    <p className="text-blue-200 text-sm">{currentStepData.tip}</p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-white/10">
            <div className="flex space-x-3">
              {currentStep > 0 && (
                <button
                  onClick={prevStep}
                  className="bg-white/10 hover:bg-white/20 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Icon name="ChevronLeft" size={16} />
                  <span>Back</span>
                </button>
              )}
              <button
                onClick={skipTour}
                className="text-white/60 hover:text-white transition-colors py-2 px-4"
              >
                Skip Setup
              </button>
            </div>
            
            <button
              onClick={nextStep}
              className="bg-[#7289DA] hover:bg-[#677BC4] text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center space-x-2"
            >
              <span>{currentStep === steps.length - 1 ? 'Finish' : 'Next'}</span>
              {currentStep < steps.length - 1 && <Icon name="ChevronRight" size={16} />}
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Username Helper Modal */}
      <UsernameHelperModal 
        isOpen={showUsernameHelper}
        onClose={() => setShowUsernameHelper(false)}
      />
    </>
  );
};

/**
 * Username Helper Modal Component
 * Shows visual guide for finding Discord username
 */
const UsernameHelperModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100001] flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Find Your Discord Username</h3>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <h4 className="text-white font-medium mb-2">📱 In Discord App/Website:</h4>
            <ol className="text-white/80 text-sm space-y-1 list-decimal list-inside">
              <li>Click your profile picture (bottom left)</li>
              <li>Your username appears under your display name</li>
              <li>Copy it exactly (case-sensitive!)</li>
            </ol>
          </div>
          
          <div className="p-3 bg-white/5 rounded-lg border border-white/10">
            <h4 className="text-white font-medium mb-2">🔍 Examples:</h4>
            <div className="text-sm space-y-1">
              <div className="text-green-300">✅ New format: <code className="bg-white/10 px-1 rounded">john.doe</code></div>
              <div className="text-green-300">✅ Legacy format: <code className="bg-white/10 px-1 rounded">john.doe#1234</code></div>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="w-full bg-[#7289DA] hover:bg-[#677BC4] text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Got it!
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DiscordOnboardingTour;